import {
  type ElementType,
  type FormEvent,
  type ReactNode,
  useState,
  useContext,
  useEffect,
} from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import NavigationPrompt from "../NavigationPrompt";
import { toastr } from "react-redux-toastr";
import ReCAPTCHA from "react-google-recaptcha";

import { paymentService } from "@/features/payments/services/paymentService";
import { LoaderContext } from "@/helpers/contexts/contexts";
import LoadingButton from "@/components/loaders/LoadingButton";
import RpMUIDialog from "../modals/RpMUIModal";
import { HTTP_STATUS } from "@/config/constants";
import { getDashboardPath } from "@/helpers/utils";
import { ENV } from "@/config/appConfig";

type CheckoutRecord = Record<string, any>;
type Loader = (_show: boolean) => void;
type CheckoutFormProps = {
  api?: (_payload: CheckoutRecord) => Promise<{ status?: number }>;
  billingDetails?: CheckoutRecord;
  data: CheckoutRecord;
  extraData?: CheckoutRecord;
  success: () => void;
};
type CardError = {
  message?: string;
};
type NavigationPromptProps = {
  onCancel: () => void;
  show: boolean;
};

const LoadingButtonComponent = LoadingButton as ElementType;

const CARD_OPTIONS: any = {
  hidePostalCode: true,
  className: "rp-stripe-card-input",
  iconStyle: "solid",
  style: {
    base: {
      fontSize: "18px",
      fontSmoothing: "antialiased",
      padding: 20,
      marginBottom: 20,
    },
  },
};

const CheckoutForm = ({
  success,
  billingDetails,
  data,
  extraData,
  api,
}: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const loader = useContext(LoaderContext) as Loader;
  const navigate = useNavigate();

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<CardError | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [paySuccess, setPaySuccess] = useState(false);
  const [captchaSuccess, onCaptchaSuccess] = useState<string | null | false>(
    false
  );

  useEffect(() => {
    loader(processing);
  }, [processing, loader]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    if (error) {
      elements.getElement("card")?.focus();
      return;
    }

    if (cardComplete) {
      setProcessing(true);
    }

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
        billing_details: billingDetails,
      });
      if (!error) {
        let res: { status?: number } | undefined;
        if (api) {
          res = await api({
            stripe_id: paymentMethod.id,
            ...data,
            // captcha: captchaSuccess,
          });
        } else {
          res = await paymentService.charge({
            stripe_id: paymentMethod.id,
            ...data,
            extraData,
            // captcha: captchaSuccess,
          });
        }
        if (res.status === HTTP_STATUS.OK) {
          setPaySuccess(true);
          success();
        }
      } else if (error && error?.message) {
        toastr.error("Error", error?.message || "Something went wrong!");
      }
    } catch (err: any) {
      const isRedirect = err?.response?.data?.redirect;
      if (isRedirect) {
        setTimeout(() => {
          setProcessing(false);
          navigate(getDashboardPath(), { replace: true });
        }, 1000);
      }
    } finally {
      loader(false);
      setProcessing(false);
    }
  };

  return paySuccess ? (
    <div className="text-center py-2 pay-success-message">
      <h4 className="mb-3 heading7">Payment successful</h4>
      <h5 className="heading09 my-2">Thank you for your payment.</h5>
    </div>
  ) : (
    <>
      <form onSubmit={handleSubmit}>
        <div className="form-group mb-3">
          <label>
            Card<sup>*</sup>
          </label>
          <CardElement
            options={CARD_OPTIONS}
            onChange={(e: { complete: boolean; error?: CardError | null }) => {
              setError(e.error);
              setCardComplete(e.complete);
            }}
          />
        </div>

        {error && <div className="error">{error.message}</div>}

        <div className="d-flex-center my-5">
          {captchaSuccess ? (
            <LoadingButtonComponent
              type="submit"
              loader={processing}
              disabled={!stripe || processing || Boolean(error)}
              text={processing ? "Processing..." : `Pay $${data.amount}`}
            />
          ) : (
            <ReCAPTCHA
              sitekey={ENV.RECAPTCHA_SITE_KEY}
              onChange={(value) => {
                onCaptchaSuccess(value);
              }}
            />
          )}
        </div>
      </form>

      <NavigationPrompt when={processing}>
        {({ onCancel, show }: NavigationPromptProps): ReactNode => (
          <RpMUIDialog open={show} onClose={onCancel} maxWidth="sm">
            <div className="text-center py-2 pay-success-message">
              <h4 className="mb-3 heading7">Please wait..</h4>
              <h5 className="heading09 my-2">Transaction is in process..</h5>
            </div>
          </RpMUIDialog>
        )}
      </NavigationPrompt>
    </>
  );
};

export default CheckoutForm;
